import { useEffect } from 'react';

type PageMeta = {
  title: string;
  description?: string;
  /** Path for canonical + og:url, e.g. "/pricing". Defaults to current path. */
  path?: string;
  /** Emit <meta name="robots" content="noindex,follow"> (e.g. redirect pages). */
  noindex?: boolean;
};

const canonicalFor = (path?: string) => {
  try {
    const isProd = window.location.hostname.endsWith('pabsync.com');
    const origin = isProd ? 'https://pabsync.com' : window.location.origin;
    return new URL(path || window.location.pathname, origin).toString();
  } catch {
    return `https://pabsync.com${path || ''}`;
  }
};

/**
 * Client-side SEO for this SPA: sets title, description, canonical, Open Graph,
 * and robots for the current page, then restores the previous values on unmount
 * so navigating away doesn't leak metadata between routes.
 */
export function usePageMeta({ title, description, path, noindex }: PageMeta) {
  useEffect(() => {
    const cleanup: Array<() => void> = [];

    const upsert = (
      selector: string,
      create: () => HTMLElement,
      attr: string,
      value: string
    ) => {
      const existing = document.head.querySelector(selector) as HTMLElement | null;
      const prev = existing?.getAttribute(attr) ?? null;
      const created = !existing;
      const el = existing || create();
      if (created) document.head.appendChild(el);
      el.setAttribute(attr, value);
      cleanup.push(() => {
        if (created) el.remove();
        else if (prev == null) el.removeAttribute(attr);
        else el.setAttribute(attr, prev);
      });
    };

    const meta = (name: string, content: string) =>
      upsert(
        `meta[name="${name}"]`,
        () => {
          const m = document.createElement('meta');
          m.setAttribute('name', name);
          return m;
        },
        'content',
        content
      );

    const prop = (property: string, content: string) =>
      upsert(
        `meta[property="${property}"]`,
        () => {
          const m = document.createElement('meta');
          m.setAttribute('property', property);
          return m;
        },
        'content',
        content
      );

    const canonical = canonicalFor(path);

    const prevTitle = document.title;
    document.title = title;
    cleanup.push(() => {
      document.title = prevTitle;
    });

    if (description) {
      meta('description', description);
      prop('og:description', description);
    }
    prop('og:title', title);
    prop('og:url', canonical);
    if (noindex) meta('robots', 'noindex,follow');

    upsert(
      'link[rel=canonical]',
      () => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'canonical');
        return l;
      },
      'href',
      canonical
    );

    return () => {
      for (let i = cleanup.length - 1; i >= 0; i--) cleanup[i]();
    };
  }, [title, description, path, noindex]);
}

export default usePageMeta;
